import React from 'react';
import SEO from '../components/SEO';
import { FaShieldAlt, FaUsers, FaChartLine, FaAward } from 'react-icons/fa';

const About: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'À propos de BetSprinto',
    description:
      'Découvrez qui nous sommes et notre mission pour vous aider à trouver les meilleures offres de paris sportifs en France.',
  };

  return (
    <>
      <SEO
        title="À Propos de BetSprinto - Votre Guide Expert des Paris Sportifs"
        description="Découvrez BetSprinto, votre guide expert pour les meilleures offres de paris sportifs en France. Transparence, expertise et indépendance."
        jsonLd={jsonLd}
      />

      <div className="min-h-screen pt-28 py-12">
        <div className="container mx-auto px-4">
          {/* Hero */}
          <section className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              À Propos de <span className="text-gradient">BetSprinto</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Votre guide expert et indépendant pour les meilleures offres de paris sportifs en
              France
            </p>
          </section>

          {/* Mission */}
          <section className="mb-16">
            <div className="glass rounded-2xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-white mb-6">Notre Mission</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-4">
                BetSprinto a été créé pour simplifier votre recherche des meilleures offres de paris
                sportifs en France. Notre équipe d'experts analyse en profondeur chaque bookmaker
                pour vous fournir des informations précises, transparentes et à jour.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Nous croyons que chaque parieur mérite d'avoir accès aux meilleures conditions,
                bonus et fonctionnalités. C'est pourquoi nous comparons en détail les plateformes
                licenciées par l'ANJ pour vous aider à faire le meilleur choix selon vos besoins.
              </p>
            </div>
          </section>

          {/* Values */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">Nos Valeurs</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: FaShieldAlt,
                  title: 'Transparence',
                  description:
                    'Nous fournissons des informations claires et honnêtes sur chaque bookmaker.',
                },
                {
                  icon: FaUsers,
                  title: 'Indépendance',
                  description:
                    'Nos avis sont basés sur notre expertise, sans influence commerciale.',
                },
                {
                  icon: FaChartLine,
                  title: 'Expertise',
                  description:
                    'Notre équipe analyse en profondeur chaque aspect des plateformes.',
                },
                {
                  icon: FaAward,
                  title: 'Qualité',
                  description:
                    'Nous nous engageons à maintenir les plus hauts standards de qualité.',
                },
              ].map((value, index) => (
                <div key={index} className="glass p-6 rounded-xl hover:bg-dark-hover transition-all">
                  <value.icon className="text-accent-primary mb-4" size={32} />
                  <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                  <p className="text-gray-400 text-sm">{value.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* How We Work */}
          <section className="mb-16">
            <div className="glass rounded-2xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-white mb-6">Comment Nous Travaillons</h2>
              <div className="space-y-6">
                {[
                  {
                    step: '1',
                    title: 'Analyse Complète',
                    description:
                      'Nous testons chaque bookmaker en profondeur : interface, cotes, bonus, support client, et fonctionnalités.',
                  },
                  {
                    step: '2',
                    title: 'Comparaison Détaillée',
                    description:
                      'Nous comparons les offres sur plusieurs critères objectifs pour vous donner une vue d\'ensemble claire.',
                  },
                  {
                    step: '3',
                    title: 'Mise à Jour Régulière',
                    description:
                      'Nous mettons à jour régulièrement nos analyses pour refléter les dernières offres et changements.',
                  },
                  {
                    step: '4',
                    title: 'Recommandations Expertes',
                    description:
                      'Basées sur nos analyses, nous vous aidons à trouver la meilleure offre selon vos préférences.',
                  },
                ].map((item, index) => (
                  <div key={index} className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="bg-accent-primary/20 text-accent-primary w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">
                        {item.step}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Responsible Gaming */}
          <section>
            <div className="glass rounded-2xl p-8 md:p-12 bg-gradient-to-r from-accent-primary/10 to-accent-secondary/10">
              <h2 className="text-3xl font-bold text-white mb-4">Jeu Responsable</h2>
              <p className="text-gray-300 text-lg mb-4">
                Chez BetSprinto, nous prenons le jeu responsable très au sérieux. Les paris
                sportifs doivent rester un divertissement et ne jamais devenir une source de
                problèmes.
              </p>
              <p className="text-gray-300 text-lg mb-6">
                Si vous pensez avoir un problème de jeu, n'hésitez pas à contacter des
                organisations d'aide comme{' '}
                <a href="https://www.joueurs-info-service.fr" className="text-accent-primary hover:underline">
                  Joueurs Info Service
                </a>{' '}
                ou{' '}
                <a href="https://www.evalujeu.fr" className="text-accent-primary hover:underline">
                  Evalujeu
                </a>
                .
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-dark-card px-4 py-2 rounded-lg">
                  <strong className="text-yellow-400">18+</strong>
                  <span className="text-gray-300 ml-2">Joueurs majeurs uniquement</span>
                </div>
                <div className="bg-dark-card px-4 py-2 rounded-lg">
                  <strong className="text-red-400">⚠️</strong>
                  <span className="text-gray-300 ml-2">Jouez de manière responsable</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default About;

